import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/App";
import {
  Check, Sparkles, Loader2, CreditCard, ArrowUp, ArrowDown, Star, Ticket,
  ShieldCheck, XCircle, Zap, RotateCcw, Trophy, Minus, Infinity as InfIcon, Lock,
} from "lucide-react";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const PLAN_TAGLINE = {
  estagiario: "Comece grátis e teste o Hermes",
  junior:     "Advogado autônomo em ritmo crescente",
  pleno:      "Escritório em consolidação",
  senior:     "Escritório com operação intensa",
  master:     "Alta performance sem limites",
};

const PLAN_HIGHLIGHT_ICON = {
  estagiario: Zap,
  junior:     Sparkles,
  pleno:      Star,
  senior:     ShieldCheck,
  master:     Sparkles,
};

// Ordem canônica (garantia visual se `order` vier faltando do backend)
const CANONICAL_ORDER = ["estagiario", "junior", "pleno", "senior", "master"];

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

// -----------------------------------------------------------------------------
// Página principal
// -----------------------------------------------------------------------------
export function PlansPage() {
  useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [ents, setEnts] = useState([]);
  const [services, setServices] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  // Cupom
  const [coupon, setCoupon] = useState("");
  const [couponState, setCouponState] = useState(null); // {valid, discount_amount, message}
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    // Toast leve baseado em query string (cancelled/success)
    if (params.get("cancelled")) toast.info("Assinatura cancelada. Você pode tentar novamente.");
    if (params.get("checkout") === "success") toast.success("Pagamento em processamento — sua assinatura será ativada em segundos.");
    // eslint-disable-next-line
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, s, sub, ent] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/services"),
        api.get("/billing/subscriptions/me"),
        api.get("/billing/entitlements"),
      ]);
      const items = (p.data.items || []).slice().sort((a, b) => {
        const ao = a.order ?? CANONICAL_ORDER.indexOf(a.code);
        const bo = b.order ?? CANONICAL_ORDER.indexOf(b.code);
        return ao - bo;
      });
      setPlans(items);
      setServices(s.data.items || []);
      setEnts(ent.data.items || []);
      setCurrent(sub.data);
    } catch (e) {
      toast.error("Falha ao carregar planos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const currentPlanCode = current?.subscription?.plan_code || current?.current_plan?.code || "estagiario";
  const currentPlan = plans.find(p => p.code === currentPlanCode);

  const applyCoupon = async () => {
    const code = (coupon || "").trim().toUpperCase();
    if (!code) { setCouponState(null); return; }
    setCouponBusy(true);
    try {
      // Preview no contexto de subscription. Base amount depende do plano — usamos R$0
      // e o backend calcula o desconto sobre o preço do plano selecionado.
      const r = await api.post("/billing/coupons/preview",
        { code },
        { params: { context: "subscription", base_amount: 0 } }
      );
      const d = r.data || {};
      if (d.valid || d.applied || d.discount_amount != null || d.percent_off != null || d.amount_off != null) {
        setCouponState({
          valid: true,
          code,
          percent_off: d.percent_off ?? d.value_percent ?? null,
          amount_off: d.amount_off ?? d.discount_amount ?? null,
          message: d.message || "Cupom válido — aplicado nos preços abaixo",
        });
      } else {
        setCouponState({ valid: false, message: d.message || d.reason || "Cupom inválido" });
      }
    } catch (e) {
      setCouponState({ valid: false, message: e?.response?.data?.detail || "Cupom inválido" });
    } finally {
      setCouponBusy(false);
    }
  };

  const clearCoupon = () => { setCoupon(""); setCouponState(null); };

  const priceWithCoupon = (price) => {
    if (!couponState?.valid || !price) return { final: price, discount: 0 };
    if (couponState.percent_off) {
      const d = (price * couponState.percent_off) / 100;
      return { final: Math.max(0, price - d), discount: d };
    }
    if (couponState.amount_off) {
      // amount_off pode vir em BRL ou centavos — normalizamos: se > preço*10 assumimos centavos
      const off = couponState.amount_off > price * 10
        ? couponState.amount_off / 100
        : couponState.amount_off;
      return { final: Math.max(0, price - off), discount: off };
    }
    return { final: price, discount: 0 };
  };

  const orderIdx = (code) => CANONICAL_ORDER.indexOf(code);
  const compareToCurrent = (planCode) => {
    if (!currentPlanCode) return "same";
    if (planCode === currentPlanCode) return "same";
    return orderIdx(planCode) > orderIdx(currentPlanCode) ? "upgrade" : "downgrade";
  };

  const checkout = async (plan) => {
    const action = compareToCurrent(plan.code);
    setBusy(plan.code);
    try {
      // Rebaixamento para Estagiário: sem checkout
      if (Number(plan.price_monthly || 0) <= 0) {
        if (action === "downgrade" && current?.subscription?.stripe_subscription_id) {
          await api.post("/billing/subscriptions/change-plan", {
            plan_code: plan.code, proration: false,
          });
          toast.success("Downgrade agendado para o fim do ciclo atual");
          await loadAll();
          return;
        }
        await api.post("/billing/subscriptions/checkout", {
          plan_code: plan.code, origin_url: window.location.origin,
        });
        toast.success("Plano Estagiário ativado");
        await loadAll();
        return;
      }
      // Upgrade/downgrade de assinatura já existente
      if (current?.subscription?.stripe_subscription_id && action !== "same") {
        await api.post("/billing/subscriptions/change-plan", {
          plan_code: plan.code, proration: true,
        });
        toast.success(action === "upgrade" ? "Upgrade aplicado com proration" : "Downgrade agendado");
        await loadAll();
        return;
      }
      // Novo checkout Stripe
      const r = await api.post("/billing/subscriptions/checkout", {
        plan_code: plan.code, origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      // Sem URL (ex.: já ativo) — apenas recarrega
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar checkout");
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!current?.subscription?.stripe_subscription_id) return;
    if (!window.confirm("Cancelar ao final do ciclo atual?")) return;
    try {
      await api.post("/billing/subscriptions/cancel");
      toast.success("Cancelamento agendado para o fim do ciclo");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cancelar"); }
  };

  // Recomendado = plano com highlight, ou pleno se nada marcado
  const featuredCode = useMemo(() => {
    const h = plans.find(p => p.highlight);
    return h?.code || "pleno";
  }, [plans]);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="plans-page">
      <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 py-8 lg:py-10">
        {/* Cabeçalho */}
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>
            Planos Hermes · Fase 8
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2" style={{ color: NAVY }}>
            Escolha o motor do seu escritório
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto text-sm">
            Assinaturas mensais em ambiente Stripe seguro. Cancele quando quiser, sem multa. Ao trocar de plano
            aplicamos proration automática.
          </p>
        </div>

        {/* Cupom */}
        <div className="max-w-xl mx-auto mb-8" data-testid="plans-coupon-block">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 flex items-center gap-2">
            <Ticket className="w-4 h-4 text-slate-500 shrink-0"/>
            <input
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
              placeholder="Tem um cupom? Digite o código"
              data-testid="plans-coupon-input"
              className="flex-1 h-9 px-2 bg-transparent text-sm text-slate-900 focus:outline-none placeholder:text-slate-400"
            />
            {couponState?.valid ? (
              <button onClick={clearCoupon} data-testid="plans-coupon-clear"
                className="h-9 px-3 rounded-md text-[12px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5"/> Remover
              </button>
            ) : (
              <button onClick={applyCoupon} disabled={couponBusy || !coupon.trim()}
                data-testid="plans-coupon-apply"
                className="h-9 px-4 rounded-md text-[12.5px] font-semibold text-white disabled:opacity-60"
                style={{ background: NAVY }}>
                {couponBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Aplicar"}
              </button>
            )}
          </div>
          {couponState && (
            <div data-testid="plans-coupon-feedback"
              className={`mt-2 text-[12px] rounded-md px-3 py-2 flex items-center gap-2 ${
                couponState.valid
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-rose-50 text-rose-800 border border-rose-200"
              }`}>
              {couponState.valid ? <Check className="w-3.5 h-3.5"/> : <XCircle className="w-3.5 h-3.5"/>}
              <span>
                {couponState.message}
                {couponState.valid && couponState.percent_off && ` (-${couponState.percent_off}%)`}
                {couponState.valid && !couponState.percent_off && couponState.amount_off && ` (-${fmtBRL((couponState.amount_off > 1000 ? couponState.amount_off / 100 : couponState.amount_off))})`}
              </span>
            </div>
          )}
        </div>

        {/* Cards de planos */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="plans-grid">
            {plans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                featured={plan.code === featuredCode}
                relation={compareToCurrent(plan.code)}
                priceCalc={priceWithCoupon}
                onCheckout={() => checkout(plan)}
                loading={busy === plan.code}
                current={plan.code === currentPlanCode}
                entitlements={ents.filter(e => e.plan_code === plan.code || !e.plan_code)}
                services={services}
              />
            ))}
          </div>
        )}

        {/* Sub info */}
        {current?.subscription && current.subscription.stripe_subscription_id && (
          <div className="mt-8 max-w-3xl mx-auto rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3" data-testid="plans-current-info">
            <div className="text-sm text-slate-700">
              Assinatura atual: <strong className="text-slate-900">{currentPlan?.name || currentPlanCode}</strong>
              {current.subscription.cancel_at_period_end && (
                <span className="ml-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                  cancela ao fim do ciclo
                </span>
              )}
            </div>
            {!current.subscription.cancel_at_period_end && (
              <button onClick={cancel} data-testid="plans-cancel-btn"
                className="h-9 px-3 rounded-lg border border-rose-300 text-rose-700 text-[12.5px] hover:bg-rose-50 inline-flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5"/> Cancelar ao fim do ciclo
              </button>
            )}
          </div>
        )}

        {/* Tabela comparativa completa */}
        {!loading && plans.length > 0 && (
          <PlansComparisonTable
            plans={plans}
            services={services}
            entitlements={ents}
            currentPlanCode={currentPlanCode}
          />
        )}

        <p className="text-center text-[11px] text-slate-500 mt-8">
          Ambiente Stripe · cartão de teste 4242 4242 4242 4242 · qualquer data futura · qualquer CVC.
        </p>
        <div className="text-center mt-2">
          <button onClick={() => navigate("/finance")} data-testid="plans-see-wallet"
            className="text-[11px] text-slate-500 hover:text-slate-900 underline decoration-dotted">
            Ver histórico de cobranças e carteira
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Card individual
// -----------------------------------------------------------------------------
function PlanCard({ plan, featured, relation, priceCalc, onCheckout, loading, current, entitlements, services }) {
  const price = Number(plan.price_monthly || 0);
  const { final, discount } = priceCalc(price);
  const Icon = PLAN_HIGHLIGHT_ICON[plan.code] || Sparkles;

  const relationBadge = current
    ? { label: "PLANO ATUAL", cls: "bg-slate-900 text-white" }
    : relation === "upgrade"
      ? { label: "UPGRADE", cls: "bg-emerald-100 text-emerald-800 border border-emerald-300", icon: ArrowUp }
      : relation === "downgrade"
        ? { label: "DOWNGRADE", cls: "bg-amber-100 text-amber-900 border border-amber-300", icon: ArrowDown }
        : null;

  const btnLabel = current
    ? "Plano atual"
    : loading
      ? "Aguarde…"
      : relation === "upgrade"
        ? "Fazer upgrade"
        : relation === "downgrade"
          ? (price <= 0 ? "Rebaixar para Estagiário" : "Fazer downgrade")
          : price <= 0
            ? "Ativar Estagiário"
            : "Assinar";

  // Franquias resumidas (top 5 serviços não-nulos) para exibir "o que vem incluso"
  const includedRows = useMemo(() => {
    const svcByCode = Object.fromEntries((services || []).map((s) => [s.code, s]));
    const rows = (entitlements || [])
      .filter(e => e.plan_code === plan.code)
      .map(e => ({
        code: e.service_code,
        name: svcByCode[e.service_code]?.name || e.service_code,
        qty: e.unlimited ? -1 : (e.included_quantity ?? 0),
      }))
      .filter(r => r.qty !== 0)
      .sort((a, b) => (a.qty === -1 ? -1 : 0) - (b.qty === -1 ? -1 : 0));
    return rows.slice(0, 6);
  }, [entitlements, services, plan.code]);

  return (
    <div data-testid={`plan-card-${plan.code}`}
      className={`relative rounded-2xl border p-5 bg-white transition ${
        featured
          ? "border-[#C9953D] shadow-[0_20px_50px_-20px_rgba(201,149,61,0.5)]"
          : "border-slate-200 shadow-sm hover:shadow-md"
      }`}>
      {featured && (
        <div className="absolute -top-3 left-3 text-[10px] font-bold uppercase tracking-widest text-white px-3 py-1 rounded-full inline-flex items-center gap-1"
             style={{ background: GOLD }}>
          <Star className="w-3 h-3"/> Recomendado
        </div>
      )}
      {relationBadge && (
        <div className={`absolute -top-2 right-3 text-[9px] font-bold px-2 py-0.5 rounded ${relationBadge.cls}`}
             data-testid={`plan-badge-${plan.code}`}>
          {relationBadge.icon && <relationBadge.icon className="w-2.5 h-2.5 inline mr-0.5"/>}
          {relationBadge.label}
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: featured ? GOLD : `${NAVY}0F`, color: featured ? "#fff" : NAVY }}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="font-heading text-xl font-semibold" style={{ color: NAVY }}>
          {plan.name}
        </div>
      </div>
      <div className="text-[11.5px] text-slate-500 min-h-[32px]">{PLAN_TAGLINE[plan.code] || ""}</div>

      <div className="mt-4">
        {discount > 0 ? (
          <>
            <div className="text-[11px] line-through text-slate-400">{fmtBRL(price)}</div>
            <div className="flex items-baseline gap-1.5">
              <div className="text-3xl font-bold" style={{ color: NAVY }} data-testid={`plan-price-${plan.code}`}>
                {fmtBRL(final)}
              </div>
              <div className="text-xs text-slate-500">/mês</div>
            </div>
            <div className="text-[10.5px] text-emerald-700 font-semibold mt-0.5">
              economia de {fmtBRL(discount)}/mês
            </div>
          </>
        ) : (
          <div className="flex items-baseline gap-1.5">
            <div className="text-3xl font-bold" style={{ color: NAVY }} data-testid={`plan-price-${plan.code}`}>
              {price === 0 ? "Grátis" : fmtBRL(price)}
            </div>
            {price > 0 && <div className="text-xs text-slate-500">/mês</div>}
          </div>
        )}
      </div>

      <ul className="mt-4 space-y-1.5 min-h-[130px]">
        {/* Arena OAB destacada em TODOS os cards */}
        <li className={`flex items-start gap-1.5 text-[12.5px] font-semibold rounded-lg px-2 py-1 -mx-1 mb-1
                       ${plan.arena_included
                         ? "bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 text-amber-900"
                         : "bg-slate-50 border border-slate-200 text-slate-500"}`}
            data-testid={`plan-arena-${plan.code}`}>
          {plan.arena_included
            ? <Trophy className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0"/>
            : <Lock className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0"/>}
          <span className="flex-1">
            Arena OAB
            <span className="ml-1 font-normal text-[11px]">
              {plan.arena_included ? "· inclusa" : "· não incluída"}
            </span>
          </span>
        </li>
        {includedRows.map((r) => (
          <li key={r.code} className="flex items-start gap-1.5 text-[12.5px] text-slate-800">
            <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0"/>
            <span>
              {r.qty === -1 ? "Ilimitado" : r.qty} <span className="text-slate-500">·</span> {r.name}
            </span>
          </li>
        ))}
        {includedRows.length === 0 && (
          <li className="text-[12px] text-slate-400 italic">Franquias exclusivas por serviço</li>
        )}
      </ul>

      <button
        onClick={onCheckout}
        disabled={loading || current}
        data-testid={`plan-cta-${plan.code}`}
        className={`mt-5 w-full h-11 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition disabled:opacity-60 ${
          featured
            ? "text-white hover:opacity-95"
            : current
              ? "bg-slate-100 text-slate-500 cursor-not-allowed"
              : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
        style={featured && !current ? { background: GOLD } : undefined}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <CreditCard className="w-4 h-4"/>}
        {btnLabel}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tabela comparativa · matriz Serviços × Planos
// -----------------------------------------------------------------------------
function PlansComparisonTable({ plans, services, entitlements, currentPlanCode }) {
  const orderedPlans = useMemo(() =>
    plans.slice().sort((a, b) => {
      const ao = a.order ?? CANONICAL_ORDER.indexOf(a.code);
      const bo = b.order ?? CANONICAL_ORDER.indexOf(b.code);
      return ao - bo;
    }), [plans]);

  // Matriz: para cada service.code, retorna qty por plan.code
  const matrix = useMemo(() => {
    const idx = {};
    for (const e of entitlements || []) {
      const svc = e.service_code;
      const plan = e.plan_code;
      if (!svc || !plan) continue;
      if (!idx[svc]) idx[svc] = {};
      idx[svc][plan] = e.unlimited ? "∞" : (e.included_quantity ?? 0);
    }
    return idx;
  }, [entitlements]);

  // Ordena serviços: ARENA_OAB primeiro, depois DJEN, depois demais alfabético
  const orderedServices = useMemo(() => {
    const priority = { ARENA_OAB: 0, DJEN: 1, SIGNATURE: 2, OCR: 3 };
    return (services || []).slice().sort((a, b) => {
      const pa = priority[a.code] ?? 99;
      const pb = priority[b.code] ?? 99;
      if (pa !== pb) return pa - pb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [services]);

  // Renderiza a célula
  const renderCell = (svcCode, planCode) => {
    // Arena OAB: usar campo direto do plano
    if (svcCode === "ARENA_OAB") {
      const plan = plans.find(p => p.code === planCode);
      if (plan?.arena_included) {
        return <Check className="w-4 h-4 text-emerald-600 mx-auto" aria-label="Incluído"/>;
      }
      return <Minus className="w-3.5 h-3.5 text-slate-300 mx-auto" aria-label="Não incluído"/>;
    }
    const v = matrix[svcCode]?.[planCode];
    if (v === "∞") return <InfIcon className="w-4 h-4 text-emerald-600 mx-auto" aria-label="Ilimitado"/>;
    if (v && Number(v) > 0) {
      return <span className="text-[12px] font-semibold text-slate-800">{v}</span>;
    }
    return <Minus className="w-3.5 h-3.5 text-slate-300 mx-auto" aria-label="Não incluído"/>;
  };

  return (
    <div className="mt-10" data-testid="plans-comparison-table">
      <div className="text-center mb-4">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>
          Comparativo completo
        </div>
        <h2 className="font-heading text-2xl mt-1" style={{ color: NAVY }}>
          O que cada plano entrega
        </h2>
        <p className="text-slate-500 text-xs mt-1">
          Franquias mensais renovadas todo ciclo · consumo excedente cobrado por serviço.
        </p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 font-semibold sticky left-0 bg-slate-50 z-10">
                Serviço
              </th>
              {orderedPlans.map(p => (
                <th key={p.code}
                    className={`px-3 py-3 text-center min-w-[110px] ${
                      p.code === currentPlanCode ? "bg-amber-50" : ""
                    }`}
                    data-testid={`comp-header-${p.code}`}>
                  <div className="text-[10.5px] uppercase tracking-widest font-bold" style={{ color: NAVY }}>
                    {p.name}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {Number(p.price_monthly || 0) === 0 ? "Grátis" : fmtBRL(p.price_monthly)}
                    {Number(p.price_monthly || 0) > 0 && <span className="text-slate-400"> /mês</span>}
                  </div>
                  {p.code === currentPlanCode && (
                    <div className="mt-1 text-[8.5px] font-bold text-amber-800 tracking-wider">SEU PLANO</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orderedServices.map((svc, i) => (
              <tr key={svc.code}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${
                    svc.code === "ARENA_OAB" ? "bg-gradient-to-r from-amber-50/40 to-yellow-50/40" : ""
                  }`}
                  data-testid={`comp-row-${svc.code}`}>
                <td className="px-4 py-2.5 text-[12.5px] text-slate-800 sticky left-0 bg-inherit z-[5]">
                  <div className="flex items-center gap-2">
                    {svc.code === "ARENA_OAB" && <Trophy className="w-3.5 h-3.5 text-amber-600 shrink-0"/>}
                    <span className={svc.code === "ARENA_OAB" ? "font-semibold" : ""}>{svc.name}</span>
                  </div>
                  {svc.code === "ARENA_OAB" && (
                    <div className="text-[10px] text-amber-700 font-medium tracking-wide mt-0.5">
                      MAIS PROCURADO · GAMIFICAÇÃO OAB
                    </div>
                  )}
                </td>
                {orderedPlans.map(p => (
                  <td key={p.code}
                      className={`px-3 py-2.5 text-center ${p.code === currentPlanCode ? "bg-amber-50/60" : ""}`}
                      data-testid={`comp-cell-${svc.code}-${p.code}`}>
                    {renderCell(svc.code, p.code)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50">
              <td className="px-4 py-2 text-[10.5px] text-slate-500 sticky left-0 bg-slate-50 z-[5]">
                Legenda:
              </td>
              <td colSpan={orderedPlans.length} className="px-3 py-2 text-[10.5px] text-slate-500">
                <span className="inline-flex items-center gap-1 mr-4">
                  <Check className="w-3 h-3 text-emerald-600"/> incluído
                </span>
                <span className="inline-flex items-center gap-1 mr-4">
                  <InfIcon className="w-3 h-3 text-emerald-600"/> ilimitado
                </span>
                <span className="inline-flex items-center gap-1 mr-4">
                  <Minus className="w-3 h-3 text-slate-300"/> não incluído
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="font-semibold text-slate-700">N</span> franquia mensal
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Success / Cancel — permanecem exportados p/ manter compatibilidade de rotas
// -----------------------------------------------------------------------------
export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status !== "paid" && attempts < 30) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        }
      } catch { toast.error("Não foi possível confirmar o pagamento"); }
    };
    poll();
  }, [sessionId, attempts]);

  const paid = status.payment_status === "paid";
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="max-w-md bg-white p-10 rounded-2xl border border-slate-100 text-center shadow-sm">
        {paid ? (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-600"/>
            </div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Pagamento confirmado</h1>
            <p className="text-sm text-slate-500 mt-2">Sua assinatura está ativa. Bem-vindo(a) ao Hermes.</p>
            <button onClick={() => navigate("/dashboard")} data-testid="btn-goto-dashboard"
              className="mt-6 w-full h-11 rounded-xl text-white text-sm font-medium hover:-translate-y-0.5 transition"
              style={{ background: NAVY }}>Ir para o Dashboard</button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4"/>
            <h1 className="font-heading text-xl font-semibold text-slate-900">Confirmando pagamento…</h1>
            <p className="text-sm text-slate-500 mt-2">Aguarde alguns segundos. Verificando com a Stripe.</p>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="max-w-md bg-white p-10 rounded-2xl border border-slate-100 text-center">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Pagamento cancelado</h1>
        <p className="text-sm text-slate-500 mt-2">Você pode retomar quando quiser.</p>
        <button onClick={() => navigate("/plans")} data-testid="btn-back-plans"
          className="mt-6 w-full h-11 rounded-xl border border-slate-200 text-sm hover:bg-slate-50 transition">Ver planos</button>
      </div>
    </div>
  );
}

export default PlansPage;
